'use client'

import { ISessionRaw } from '@ftd/types'
import { formatDate } from '@ftd/utils'
import { ActionIcon, Box, Flex, Group, Loader, Text, Tooltip } from '@mantine/core'
import {
  IconCalendar,
  IconCopy,
  IconFish,
  IconLocation,
  IconNote,
  IconPhoto,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react'
import { createColumnHelper, flexRender, getCoreRowModel, Row, useReactTable } from '@tanstack/react-table'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useDeleteSession } from '@/api/session/use-delete-session'
import { useDuplicateSession } from '@/api/session/use-duplicate-session'
import { useSessionsInfinite } from '@/api/session/use-sessions'
import { Button } from '@/components/atoms/Button'
import { CatchSummary } from '@/components/molecules/catch-summary/catch-summary'
import { ConfirmationModal } from '@/components/molecules/confirmation-modal'
import { Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from '@/components/molecules/Table'
import { ROUTES } from '@/constants/routes'
import { useNotifications } from '@/hooks/useNotifications'

export const Sessions: React.FC = () => {
  const { data, fetchNextPage, hasNextPage, isLoading } = useSessionsInfinite()
  const router = useRouter()
  const t = useTranslations()
  const { notifySuccess, notifyError } = useNotifications()
  const { mutateAsync: deleteSession } = useDeleteSession()
  const { mutate: duplicateSession } = useDuplicateSession()
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null)

  const sessions = useMemo(() => data?.pages.flatMap((page) => page.docs) || [], [data])

  const columnHelper = createColumnHelper<ISessionRaw>()

  const columns = useMemo(
    () => [
      {
        id: 'index',
        header: () => t('common.no'),
        size: 20,
        cell: ({ row }: { row: Row<ISessionRaw> }) => row.index + 1,
      },
      columnHelper.accessor('name', {
        cell: (info) => (
          <Text size="sm" lineClamp={2}>
            {info.getValue()}
          </Text>
        ),
        header: () => (
          <Flex align="center" gap="6px">
            <IconNote size={16} style={{ flexShrink: 0 }} /> {t('common.name')}
          </Flex>
        ),
      }),
      columnHelper.accessor('dateStart', {
        cell: (info) => formatDate(info.getValue()),
        header: () => (
          <Flex align="center" gap="6px">
            <IconCalendar size={16} style={{ flexShrink: 0 }} /> {t('common.date_start')}
          </Flex>
        ),
      }),
      columnHelper.accessor('dateEnd', {
        cell: (info) => formatDate(info.getValue()),
        header: () => (
          <Flex align="center" gap="6px">
            <IconCalendar size={16} style={{ flexShrink: 0 }} /> {t('common.date_end')}
          </Flex>
        ),
      }),
      columnHelper.accessor('location', {
        cell: (info) => (
          <Text size="sm" lineClamp={2}>
            {info.getValue()?.name}
          </Text>
        ),
        header: () => (
          <Flex align="center" gap="6px">
            <IconLocation size={16} style={{ flexShrink: 0 }} /> {t('common.location')}
          </Flex>
        ),
      }),
      {
        id: 'albumCount',
        header: () => (
          <Flex align="center" gap="6px">
            <IconPhoto size={16} style={{ flexShrink: 0 }} /> {t('common.albums')}
          </Flex>
        ),
        cell: ({ row }: { row: Row<ISessionRaw> }) => row.original.photoAlbums?.length ?? 0,
      },
      {
        id: 'catchCount',
        header: () => (
          <Flex align="center" gap="6px">
            <IconFish size={16} style={{ flexShrink: 0 }} /> {t('common.catches')}
          </Flex>
        ),
        cell: ({ row }: { row: Row<ISessionRaw> }) => row.original.catches?.length ?? 0,
      },
      {
        id: 'actions',
        header: () => (
          <Flex align="center" gap="6px">
            <IconSettings size={16} style={{ flexShrink: 0 }} /> {t('common.actions')}
          </Flex>
        ),
        cell: ({ row }: { row: Row<ISessionRaw> }) => (
          <Flex gap="xs">
            <Tooltip label={t('common.duplicate')}>
              <ActionIcon
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation()
                  duplicateSession(row.original._id)
                }}
              >
                <IconCopy size={16} style={{ flexShrink: 0 }} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('common.delete')}>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={(e) => {
                  e.stopPropagation()
                  setSessionToDelete(row.original._id)
                }}
              >
                <IconTrash size={16} style={{ flexShrink: 0 }} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    [columnHelper, t, duplicateSession],
  )

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return

    try {
      await deleteSession(sessionToDelete)
      notifySuccess(t('session.delete_success'))
      setSessionToDelete(null)
    } catch {
      notifyError(t('session.delete_error'))
    }
  }

  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const currentRef = loadMoreRef.current

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '0px 0px 300px 0px' },
    )

    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [hasNextPage, fetchNextPage])

  const hasSessions = !isLoading && sessions.length > 0

  return (
    <>
      <Box w="100%" style={{ position: 'relative' }} mb="0">
        {hasSessions && (
          <Box style={{ overflowX: 'auto' }}>
            <Table mb="sm">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHeaderCell key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHeaderCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row: Row<ISessionRaw>) => (
                  <TableRow
                    key={row.id}
                    onClick={() => router.push(ROUTES.SESSION_DETAIL(String(row.original._id)))}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Group justify="center" mt="lg" gap="0" style={{ position: 'sticky', bottom: 0 }}>
          <CatchSummary sessionIds={sessions.map((session) => session._id)} compact />
        </Group>

        {isLoading && (
          <Flex justify="center" my="xl">
            <Loader size="sm" />
          </Flex>
        )}
        {hasNextPage && (
          <div ref={loadMoreRef} style={{ visibility: 'hidden' }}>
            <Button onClick={() => fetchNextPage()}>{t('common.load_more')}</Button>
          </div>
        )}
        {sessions && !hasSessions && (
          <Flex justify="center" my="xl">
            {t('session.no_sessions')}
          </Flex>
        )}

        <ConfirmationModal
          opened={!!sessionToDelete}
          onClose={() => setSessionToDelete(null)}
          title={t('session.delete_confirm_title')}
          closeButtonLabel={t('common.cancel')}
          onConfirm={handleDeleteSession}
          confirmButtonColor="red"
        >
          <Text>{t('session.delete_confirm_message')}</Text>
          <Text mt="sm" c="red">
            {t('session.delete_warning')}
          </Text>
        </ConfirmationModal>
      </Box>
    </>
  )
}
